import { ErrorHandler, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

/**
 * Patterns that identify a failed dynamic import of a lazy-loaded chunk.
 *
 * These fire after a new deploy: the running index.html references chunk hashes
 * the server no longer has, so the router's `import()` rejects. The exact wording
 * depends on the browser engine:
 *   - Chromium: "Failed to fetch dynamically imported module"
 *   - Firefox:  "error loading dynamically imported module"  (DOORSPOT-UI-D1/D2)
 *   - WebKit (Safari / Firefox iOS): "Importing a module script failed" (DOORSPOT-UI-D5)
 *   - Older webpack bundles: "ChunkLoadError" / "Loading chunk N failed"
 *
 * A second shape exists when the server answers the vanished chunk URL with
 * index.html instead of a 404 — the request succeeds, so the browser complains
 * about the MIME type rather than naming the import (DOORSPOT-UI-CZ):
 *   - WebKit:   "'text/html' is not a valid JavaScript MIME type"
 *   - Chromium: "Failed to load module script: Expected a JavaScript module
 *                script but the server responded with a MIME type of ..."
 * This is the same stale deploy, so it takes the same single-reload recovery.
 */
const CHUNK_LOAD_ERROR_PATTERNS: RegExp[] = [
    /error loading dynamically imported module/i,
    /failed to fetch dynamically imported module/i,
    /importing a module script failed/i,
    /ChunkLoadError/i,
    /Loading chunk [^\s]+ failed/i,
    /Loading CSS chunk/i,
    /is not a valid JavaScript MIME type/i,
    /Failed to load module script/i,
];

/**
 * If a chunk error recurs within this window of an auto-reload, the reload didn't
 * fix it (truly-missing asset, offline, or a genuine bug) — so we stop looping and
 * report the error instead of reloading again.
 */
const RELOAD_COOLDOWN_MS = 10_000;

/** sessionStorage key holding the epoch-ms of the last recovery reload. */
const RELOAD_TIMESTAMP_KEY = 'rf:chunk-reload-at';

/**
 * URL query marker carrying the reload timestamp across the reload when
 * sessionStorage is unavailable. Consumed and stripped on the next page load.
 */
const RELOAD_MARKER_PARAM = 'rf_chunk_reload';

/**
 * Global {@link ErrorHandler} that transparently recovers from stale-deploy chunk
 * load failures with a single full page reload, and forwards everything else to
 * Sentry's handler (preserving the previous production behaviour).
 *
 * The anti-loop guard must survive the reload it triggers, so it persists a
 * timestamp in sessionStorage; when storage is unavailable (e.g. some private
 * browsing modes) it falls back to a one-shot URL query marker so a genuinely
 * broken deploy can't reload forever.
 *
 * Registered only in production (see `sentryForProd` in `app.module.ts`), where
 * hashed chunk filenames make this failure mode possible.
 */
@Injectable()
export class ChunkLoadErrorHandler implements ErrorHandler {
    /** Sentry's handler — used for every error we don't auto-recover. */
    private readonly delegate: ErrorHandler = Sentry.createErrorHandler({
        showDialog: false,
        logErrors: true,
    });

    /** Guards against a tight reload loop within a single page lifetime. */
    private reloadedThisInstance = false;

    /**
     * Epoch-ms of the reload that produced *this* page load, if any. Read (and
     * cleaned) once at construction from sessionStorage, or from the URL marker
     * when storage was unavailable when the reload was triggered.
     */
    private readonly priorReloadAt: number | null;

    constructor() {
        this.priorReloadAt = this.consumePriorReloadMarker();
    }

    handleError(error: unknown): void {
        if (this.isChunkLoadError(error)) {
            this.recoverFromChunkLoadError(error);
            return;
        }

        this.delegate.handleError(error);
    }

    private isChunkLoadError(error: unknown): boolean {
        const message = this.messageOf(error);
        return (
            !!message &&
            CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message))
        );
    }

    /**
     * Flattens an unknown thrown value into a single searchable string. Angular /
     * zone.js often wrap the real error in a `rejection` / `ngOriginalError`
     * property, so we walk those too (bounded depth to avoid pathological cycles).
     */
    private messageOf(error: unknown, depth = 0): string {
        if (error == null || depth > 4) {
            return '';
        }
        if (typeof error === 'string') {
            return error;
        }

        const wrapper = error as {
            message?: unknown;
            rejection?: unknown;
            ngOriginalError?: unknown;
            originalError?: unknown;
            cause?: unknown;
        };

        const parts = [
            typeof wrapper.message === 'string' ? wrapper.message : '',
            this.messageOf(wrapper.rejection, depth + 1),
            this.messageOf(wrapper.ngOriginalError, depth + 1),
            this.messageOf(wrapper.originalError, depth + 1),
            this.messageOf(wrapper.cause, depth + 1),
        ].filter(Boolean);

        if (parts.length > 0) {
            return parts.join(' ');
        }

        try {
            return String(error);
        } catch {
            return '';
        }
    }

    private recoverFromChunkLoadError(error: unknown): void {
        const now = this.now();

        // A reload already happened — either earlier in this page's life, or on a
        // recent prior page — yet the chunk error is still here. Reloading again
        // would loop, so hand it to Sentry instead: a broken deploy stays visible.
        if (
            this.reloadedThisInstance ||
            (this.priorReloadAt !== null &&
                now - this.priorReloadAt < RELOAD_COOLDOWN_MS)
        ) {
            this.delegate.handleError(error);
            return;
        }

        this.reloadedThisInstance = true;
        this.triggerRecoveryReload(now);
    }

    /**
     * Persist the reload marker so the next page load recognises it just reloaded,
     * then do a full reload. sessionStorage is primary; if it's unavailable we
     * carry the marker in the URL instead so the anti-loop guard still survives.
     */
    private triggerRecoveryReload(now: number): void {
        if (this.persistReloadTimestamp(now)) {
            this.reload();
            return;
        }

        this.reloadWithUrlMarker(now);
    }

    /** Reads and clears the prior-reload timestamp from storage, then the URL. */
    private consumePriorReloadMarker(): number | null {
        const fromStorage = this.readAndClearStoredTimestamp();
        const fromUrl = this.readAndStripUrlMarker();
        // Storage is primary; the URL marker only carries a value when storage was
        // unavailable at reload time. Prefer whichever is present.
        return fromStorage ?? fromUrl;
    }

    private readAndClearStoredTimestamp(): number | null {
        try {
            const raw = window.sessionStorage.getItem(RELOAD_TIMESTAMP_KEY);
            window.sessionStorage.removeItem(RELOAD_TIMESTAMP_KEY);
            return this.parseTimestamp(raw);
        } catch {
            return null;
        }
    }

    private persistReloadTimestamp(now: number): boolean {
        try {
            window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(now));
            return true;
        } catch {
            return false;
        }
    }

    private readAndStripUrlMarker(): number | null {
        let url: URL;
        try {
            url = new URL(this.readCurrentHref());
        } catch {
            return null;
        }

        const raw = url.searchParams.get(RELOAD_MARKER_PARAM);
        if (raw === null) {
            return null;
        }

        // Strip it so it doesn't linger in the address bar or leak into router state.
        url.searchParams.delete(RELOAD_MARKER_PARAM);
        try {
            this.replaceHistoryHref(url.toString());
        } catch {
            // history unavailable — the value is still consumed below.
        }

        return this.parseTimestamp(raw);
    }

    private reloadWithUrlMarker(now: number): void {
        let href: string;
        try {
            const url = new URL(this.readCurrentHref());
            url.searchParams.set(RELOAD_MARKER_PARAM, String(now));
            href = url.toString();
        } catch {
            // Couldn't build a marker URL — fall back to a plain reload. The
            // per-instance guard still prevents a loop within this page lifetime.
            this.reload();
            return;
        }

        this.navigateTo(href);
    }

    private parseTimestamp(raw: string | null): number | null {
        if (raw === null) {
            return null;
        }
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
    }

    // --- Seams for tests / isolation from the DOM ----------------------------

    /** Current wall-clock time in epoch-ms. */
    protected now(): number {
        return Date.now();
    }

    /** Full reload — fetches a fresh index.html referencing the current chunk hashes. */
    protected reload(): void {
        window.location.reload();
    }

    protected readCurrentHref(): string {
        return window.location.href;
    }

    /** Full navigation to `href` (used to carry the reload marker in the URL). */
    protected navigateTo(href: string): void {
        window.location.replace(href);
    }

    protected replaceHistoryHref(href: string): void {
        window.history.replaceState(window.history.state, '', href);
    }
}
